import { COSTS, tradeRatio, validCitySpots, validRoadSpots, validSettlementSpots } from '../engine/game.js';
import { DevCardType, GameState, RESOURCES, Resource, ResourceCounts } from '../engine/types.js';
import type { GameView } from '../engine/view.js';
import type { ServerMessage } from '../server/app.js';
import { Highlight, TERRAIN_COLORS, TERRAIN_LABELS, renderBoard } from './board.js';

// ---------- helpers ----------

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function zero(): ResourceCounts {
  return { wood: 0, brick: 0, sheep: 0, wheat: 0, ore: 0 };
}

function total(rc: Partial<ResourceCounts>): number {
  return RESOURCES.reduce((n, r) => n + (rc[r] ?? 0), 0);
}

function canAfford(have: ResourceCounts | undefined, cost: Partial<ResourceCounts>): boolean {
  return !!have && RESOURCES.every((r) => have[r] >= (cost[r] ?? 0));
}

function chip(r: Resource, n?: number): string {
  return `<span class="chip" style="--c:${TERRAIN_COLORS[r]}">${TERRAIN_LABELS[r]}${n !== undefined ? ` <b>${n}</b>` : ''}</span>`;
}

function describe(rc: Partial<ResourceCounts>): string {
  return RESOURCES.filter((r) => (rc[r] ?? 0) > 0).map((r) => chip(r, rc[r])).join(' ') || 'nothing';
}

/** The engine's placement helpers only read public parts of the state, so a view is enough for them. */
const asState = (v: GameView) => v as unknown as GameState;

const DEV_LABELS: Record<DevCardType, string> = {
  knight: 'Knight',
  roadBuilding: 'Road Building',
  yearOfPlenty: 'Year of Plenty',
  monopoly: 'Monopoly',
  victoryPoint: 'Victory Point',
};

const DEV_HELP: Record<DevCardType, string> = {
  knight: 'Move the robber and steal a card.',
  roadBuilding: 'Build 2 roads for free.',
  yearOfPlenty: 'Take any 2 resources from the bank.',
  monopoly: 'Take every card of one resource from all players.',
  victoryPoint: 'Worth 1 point. It counts automatically.',
};

// ---------- lobby ----------

function renderLobby(root: HTMLElement): void {
  root.innerHTML = `
    <main class="lobby">
      <h1>Settle</h1>
      <p class="muted">A four-player island trading game. Enter everyone's name to get a private link for each player.</p>
      <form id="new-game">
        ${[1, 2, 3, 4].map((i) => `<label>Player ${i}<input name="p${i}" maxlength="24" required placeholder="Name"></label>`).join('')}
        <label>Host password <span class="muted">(only if the server has one)</span><input name="password" type="password"></label>
        <button type="submit" class="primary">Create game</button>
        <p class="error" id="lobby-error"></p>
      </form>
      <section id="links" hidden></section>
    </main>`;

  const form = root.querySelector<HTMLFormElement>('#new-game')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const names = [1, 2, 3, 4].map((i) => String(data.get(`p${i}`)).trim());
    const res = await fetch('/api/games', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ names, password: data.get('password') || undefined }),
    });
    const body = await res.json();
    if (!res.ok) {
      root.querySelector('#lobby-error')!.textContent = body.error ?? 'Something went wrong.';
      return;
    }
    form.hidden = true;
    const links = root.querySelector<HTMLElement>('#links')!;
    links.hidden = false;
    links.innerHTML = `
      <h2>Game created</h2>
      <p class="muted">Send each player their own link. Anyone with a link can play that seat, so keep them private.</p>
      ${body.seats
        .map(
          (s: { name: string; path: string }) => `
        <div class="link-row">
          <strong>${esc(s.name)}</strong>
          <input readonly value="${esc(location.origin + s.path)}">
          <button data-copy="${esc(location.origin + s.path)}">Copy</button>
          <a href="${esc(s.path)}">Open</a>
        </div>`,
        )
        .join('')}`;
    links.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('[data-copy]');
      if (!btn) return;
      navigator.clipboard?.writeText(btn.dataset.copy!);
      btn.textContent = 'Copied';
    });
  });
}

// ---------- game page ----------

interface UiState {
  view: GameView | null;
  online: boolean[];
  mode: 'road' | 'settlement' | 'city' | null;
  robberHex: number | null;
  give: ResourceCounts;
  get: ResourceCounts;
  discard: ResourceCounts;
  selects: Record<string, string>;
  error: string;
  connected: boolean;
}

function startGame(root: HTMLElement, gameId: string, seat: string | null): void {
  root.innerHTML = `
    <div class="game">
      <div class="board-wrap">
        <div id="status" class="status"></div>
        <svg id="board" xmlns="http://www.w3.org/2000/svg"></svg>
        <div class="legend">${(['wood', 'brick', 'sheep', 'wheat', 'ore'] as const).map((r) => chip(r)).join(' ')}
          <span class="muted small">Harbor circles: white = trade any 3:1, colored = 2:1 for that resource.</span></div>
      </div>
      <aside id="panel"></aside>
    </div>`;
  const svg = root.querySelector<SVGSVGElement>('#board')!;
  const panel = root.querySelector<HTMLElement>('#panel')!;
  const statusEl = root.querySelector<HTMLElement>('#status')!;

  const ui: UiState = {
    view: null,
    online: [false, false, false, false],
    mode: null,
    robberHex: null,
    give: zero(),
    get: zero(),
    discard: zero(),
    selects: { bankGive: 'wood', bankGet: 'brick', yop1: 'wood', yop2: 'wood', mono: 'wood' },
    error: '',
    connected: false,
  };

  let socket: WebSocket | null = null;
  let errorTimer: number | undefined;

  function connect(): void {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const params = new URLSearchParams({ game: gameId, ...(seat ? { seat } : {}) });
    socket = new WebSocket(`${proto}://${location.host}/ws?${params}`);
    socket.onopen = () => {
      ui.connected = true;
      render();
    };
    socket.onmessage = (ev) => {
      const msg = JSON.parse(ev.data) as ServerMessage;
      if (msg.type === 'state') {
        const prevTrade = JSON.stringify(ui.view?.trade ?? null);
        ui.view = msg.view;
        if (JSON.stringify(msg.view.trade) !== prevTrade && msg.view.trade === null) {
          ui.give = zero();
          ui.get = zero();
        }
        if (msg.view.turnStep !== 'moveRobber') ui.robberHex = null;
        if (msg.view.you === null || msg.view.pendingDiscards[msg.view.you] === undefined) ui.discard = zero();
        if (msg.view.freeRoads > 0 && msg.view.currentPlayer === msg.view.you) ui.mode = 'road';
      } else if (msg.type === 'presence') {
        ui.online = msg.online;
      } else if (msg.type === 'error') {
        showError(msg.message);
      }
      render();
    };
    socket.onclose = () => {
      ui.connected = false;
      render();
      setTimeout(connect, 1500);
    };
  }

  function showError(message: string): void {
    ui.error = message;
    clearTimeout(errorTimer);
    errorTimer = window.setTimeout(() => {
      ui.error = '';
      render();
    }, 4000);
  }

  function send(action: Record<string, unknown>): void {
    if (socket?.readyState !== WebSocket.OPEN) {
      showError('Not connected. Reconnecting...');
      render();
      return;
    }
    socket.send(JSON.stringify({ type: 'action', action }));
  }

  // ----- derived state -----

  function isMyTurn(v: GameView): boolean {
    return v.you !== null && v.currentPlayer === v.you && v.phase !== 'ended';
  }

  function highlight(v: GameView): Highlight {
    if (!isMyTurn(v)) return null;
    const me = v.you!;
    if (v.phase === 'setup') {
      return v.setup.step === 'settlement'
        ? { kind: 'vertex', ids: validSettlementSpots(asState(v), me) }
        : { kind: 'edge', ids: validRoadSpots(asState(v), me) };
    }
    if (v.turnStep === 'moveRobber' && ui.robberHex === null) {
      return { kind: 'hex', ids: v.board.hexes.map((h) => h.id).filter((h) => h !== v.robberHex) };
    }
    if (v.turnStep !== 'actions') return null;
    if (ui.mode === 'road') return { kind: 'edge', ids: validRoadSpots(asState(v), me) };
    if (ui.mode === 'settlement') return { kind: 'vertex', ids: validSettlementSpots(asState(v), me) };
    if (ui.mode === 'city') return { kind: 'vertex', ids: validCitySpots(asState(v), me) };
    return null;
  }

  function victimsAt(v: GameView, hex: number): number[] {
    const out = new Set<number>();
    for (const vx of v.board.hexes[hex].vertices) {
      const b = v.buildings[vx];
      if (b && b.player !== v.you && v.players[b.player].resourceCount > 0) out.add(b.player);
    }
    return [...out];
  }

  function statusText(v: GameView): string {
    const cur = v.players[v.currentPlayer];
    const who = `<span class="dot" style="background:${cur.color}"></span>`;
    if (v.phase === 'ended') return `🏆 ${esc(v.players[v.winner!].name)} wins!`;
    if (v.you !== null && v.pendingDiscards[v.you] !== undefined) {
      return `You rolled over 7 cards: discard ${v.pendingDiscards[v.you]}.`;
    }
    if (!isMyTurn(v)) {
      if (v.turnStep === 'discard') return `Waiting for players to discard...`;
      return `${who} ${esc(cur.name)}'s turn`;
    }
    if (v.phase === 'setup') {
      return v.setup.step === 'settlement'
        ? `${who} Your turn: place a settlement (click a highlighted corner).`
        : `${who} Your turn: place a road next to it.`;
    }
    switch (v.turnStep) {
      case 'roll':
        return `${who} Your turn: roll the dice.`;
      case 'discard':
        return `${who} Waiting for players to discard...`;
      case 'moveRobber':
        return ui.robberHex === null ? `${who} Move the robber: click a tile.` : `${who} Choose who to steal from.`;
      case 'actions':
        if (v.freeRoads > 0) return `${who} Place ${v.freeRoads} free road${v.freeRoads > 1 ? 's' : ''}.`;
        if (ui.mode) return `${who} Click a highlighted spot to build a ${ui.mode}, or press Cancel.`;
        return `${who} Build, trade, or end your turn.`;
    }
  }

  // ----- panel sections -----

  function playersSection(v: GameView): string {
    return `<section class="players">${v.players
      .map((p, i) => {
        const tags = [
          v.longestRoad?.player === i ? '<span class="tag">Longest road</span>' : '',
          v.largestArmy?.player === i ? '<span class="tag">Largest army</span>' : '',
        ].join('');
        return `
        <div class="player ${i === v.currentPlayer ? 'current' : ''}">
          <span class="dot" style="background:${p.color}"></span>
          <span class="name">${esc(p.name)}${i === v.you ? ' (you)' : ''}${ui.online[i] ? '' : ' <span class="muted">offline</span>'}</span>
          <span class="pts" title="Victory points">${p.points} pts</span>
          <span class="muted small">${p.resourceCount} cards · dev ${p.devCardCount} · knights ${p.knightsPlayed}</span>
          ${tags}
        </div>`;
      })
      .join('')}</section>`;
  }

  function handSection(v: GameView): string {
    const me = v.you !== null ? v.players[v.you] : null;
    if (!me?.resources) return `<section><p class="muted">You're watching this game.</p></section>`;
    return `<section><h3>Your hand</h3><div class="hand">${RESOURCES.map((r) => chip(r, me.resources![r])).join('')}</div>
      <p class="muted small">Pieces left: ${me.roadsLeft} roads, ${me.settlementsLeft} settlements, ${me.citiesLeft} cities</p></section>`;
  }

  function stepper(draft: 'give' | 'get' | 'discard', counts: ResourceCounts): string {
    return `<div class="steppers">${RESOURCES.map(
      (r) => `<div class="stepper">${chip(r)}
        <button data-act="step" data-draft="${draft}" data-res="${r}" data-delta="-1">−</button>
        <span>${counts[r]}</span>
        <button data-act="step" data-draft="${draft}" data-res="${r}" data-delta="1">+</button></div>`,
    ).join('')}</div>`;
  }

  function select(key: string, options: Array<[string, string]>): string {
    return `<select data-sel="${key}">${options
      .map(([value, label]) => `<option value="${value}" ${ui.selects[key] === value ? 'selected' : ''}>${label}</option>`)
      .join('')}</select>`;
  }

  const resourceOptions = RESOURCES.map((r) => [r, TERRAIN_LABELS[r]] as [string, string]);

  function actionsSection(v: GameView): string {
    if (v.you === null || v.phase !== 'main') return '';
    const me = v.players[v.you];
    const parts: string[] = [];

    const owed = v.pendingDiscards[v.you];
    if (owed !== undefined) {
      parts.push(`<section class="highlight"><h3>Discard ${owed} cards</h3>${stepper('discard', ui.discard)}
        <button class="primary" data-act="discard" ${total(ui.discard) === owed ? '' : 'disabled'}>Discard ${total(ui.discard)}/${owed}</button></section>`);
    }

    if (isMyTurn(v)) {
      if (v.turnStep === 'roll') {
        parts.push(`<section><button class="primary big" data-act="roll">🎲 Roll dice</button></section>`);
      }
      if (v.turnStep === 'moveRobber' && ui.robberHex !== null) {
        const victims = victimsAt(v, ui.robberHex);
        parts.push(`<section class="highlight"><h3>Steal from</h3>${victims
          .map((p) => `<button data-act="steal" data-arg="${p}"><span class="dot" style="background:${v.players[p].color}"></span>${esc(v.players[p].name)}</button>`)
          .join(' ')} <button data-act="robber-back">Pick another tile</button></section>`);
      }
      if (v.turnStep === 'actions') {
        const build = (kind: 'road' | 'settlement' | 'city', label: string, cost: Partial<ResourceCounts>) => {
          const free = kind === 'road' && v.freeRoads > 0;
          const ok = free || canAfford(me.resources, cost);
          return `<button data-act="mode" data-arg="${kind}" class="${ui.mode === kind ? 'active' : ''}" ${ok ? '' : 'disabled'}
            title="Costs: ${RESOURCES.filter((r) => cost[r]).map((r) => `${cost[r]} ${r}`).join(', ')}">${label}</button>`;
        };
        parts.push(`<section><h3>Build</h3><div class="row">
          ${build('road', 'Road', COSTS.road)}
          ${build('settlement', 'Settlement', COSTS.settlement)}
          ${build('city', 'City', COSTS.city)}
          <button data-act="buy-dev" ${canAfford(me.resources, COSTS.devCard) && v.devDeckCount > 0 ? '' : 'disabled'}
            title="Costs: 1 sheep, 1 wheat, 1 ore">Dev card (${v.devDeckCount})</button>
          ${ui.mode ? '<button data-act="mode" data-arg="">Cancel</button>' : ''}
        </div>
        <p class="muted small">Road: wood + brick · Settlement: wood + brick + sheep + wheat · City: 2 wheat + 3 ore · Dev card: sheep + wheat + ore</p></section>`);

        const ratio = (r: Resource) => tradeRatio(asState(v), v.you!, r);
        const giveR = ui.selects.bankGive as Resource;
        parts.push(`<section><h3>Trade with the bank</h3><div class="row">
          Give ${ratio(giveR)} ${select('bankGive', resourceOptions)} for 1 ${select('bankGet', resourceOptions)}
          <button data-act="bank">Trade</button></div>
          <p class="muted small">Your rates: ${RESOURCES.map((r) => `${TERRAIN_LABELS[r]} ${ratio(r)}:1`).join(' · ')}</p></section>`);

        if (!v.trade) {
          parts.push(`<section><h3>Offer a trade to players</h3>
            <p class="small">You give</p>${stepper('give', ui.give)}
            <p class="small">You get</p>${stepper('get', ui.get)}
            <button data-act="offer" ${total(ui.give) && total(ui.get) ? '' : 'disabled'}>Offer trade</button></section>`);
        }
      }
    }

    if (v.trade) {
      const t = v.trade;
      const mine = t.from === v.you;
      const from = v.players[t.from];
      let body = `<p>${esc(from.name)} gives ${describe(t.give)}<br>and wants ${describe(t.get)}</p>`;
      if (mine) {
        body += v.players
          .map((p, i) => {
            if (i === t.from) return '';
            const r = t.responses[i];
            const state = r === undefined ? '<span class="muted">thinking...</span>' : r ? 'accepted' : 'declined';
            return `<div class="row">${esc(p.name)}: ${state} ${r ? `<button class="primary" data-act="confirm" data-arg="${i}">Trade with ${esc(p.name)}</button>` : ''}</div>`;
          })
          .join('');
        body += `<button data-act="cancel-trade">Withdraw offer</button>`;
      } else {
        const answered = t.responses[v.you];
        body += `<div class="row"><button class="primary" data-act="respond" data-arg="1" ${answered === true ? 'disabled' : ''}>Accept</button>
          <button data-act="respond" data-arg="0" ${answered === false ? 'disabled' : ''}>Decline</button>
          ${answered === undefined ? '' : `<span class="muted">You ${answered ? 'accepted' : 'declined'}.</span>`}</div>`;
      }
      parts.push(`<section class="highlight"><h3>Trade offer</h3>${body}</section>`);
    }

    if (me.devCards?.length) {
      const canPlayNow = isMyTurn(v) && !v.devCardPlayedThisTurn;
      parts.push(`<section><h3>Development cards</h3>${me.devCards
        .map((c, idx) => {
          const fresh = c.boughtOnTurn >= v.turn;
          const stepOk = c.type === 'knight' ? ['roll', 'actions'].includes(v.turnStep) : v.turnStep === 'actions';
          const playable = c.type !== 'victoryPoint' && canPlayNow && !fresh && stepOk;
          let extra = '';
          if (c.type === 'yearOfPlenty') extra = `${select('yop1', resourceOptions)} ${select('yop2', resourceOptions)}`;
          if (c.type === 'monopoly') extra = select('mono', resourceOptions);
          return `<div class="dev"><strong>${DEV_LABELS[c.type]}</strong> <span class="muted small">${DEV_HELP[c.type]}${fresh && c.type !== 'victoryPoint' ? ' (playable next turn)' : ''}</span>
            ${c.type === 'victoryPoint' ? '' : `<div class="row">${extra}<button data-act="play" data-arg="${c.type}" data-idx="${idx}" ${playable ? '' : 'disabled'}>Play</button></div>`}</div>`;
        })
        .join('')}</section>`);
    }

    if (isMyTurn(v) && v.turnStep === 'actions') {
      parts.push(`<section><button class="big" data-act="end">End turn</button></section>`);
    }
    return parts.join('');
  }

  function logSection(v: GameView): string {
    const dice = v.dice ? `<div class="dice">🎲 ${v.dice[0]} + ${v.dice[1]} = <b>${v.dice[0] + v.dice[1]}</b></div>` : '';
    return `<section class="log"><h3>Game log</h3>${dice}<ol reversed>${v.log
      .slice(-40)
      .reverse()
      .map((l) => `<li>${esc(l)}</li>`)
      .join('')}</ol></section>`;
  }

  // ----- render -----

  function render(): void {
    const v = ui.view;
    if (!v) {
      statusEl.textContent = ui.connected ? 'Loading...' : 'Connecting...';
      return;
    }
    if (v.phase !== 'main' || v.turnStep !== 'actions' || !isMyTurn(v)) ui.mode = null;

    renderBoard(svg, v, highlight(v), {
      onVertex: (id) => {
        if (v.phase === 'setup') send({ type: 'placeSettlement', vertex: id });
        else if (ui.mode === 'settlement') send({ type: 'buildSettlement', vertex: id });
        else if (ui.mode === 'city') send({ type: 'buildCity', vertex: id });
        ui.mode = null;
      },
      onEdge: (id) => {
        if (v.phase === 'setup') send({ type: 'placeRoad', edge: id });
        else send({ type: 'buildRoad', edge: id });
        ui.mode = null;
      },
      onHex: (id) => {
        const victims = victimsAt(v, id);
        if (victims.length <= 1) send({ type: 'moveRobber', hex: id, victim: victims[0] ?? null });
        else {
          ui.robberHex = id;
          render();
        }
      },
    });

    statusEl.innerHTML = `${statusText(v)}${ui.connected ? '' : ' <span class="error">(reconnecting...)</span>'}${
      ui.error ? `<div class="error">${esc(ui.error)}</div>` : ''
    }`;
    panel.innerHTML = playersSection(v) + handSection(v) + actionsSection(v) + logSection(v);
  }

  panel.addEventListener('change', (e) => {
    const sel = (e.target as HTMLElement).closest<HTMLSelectElement>('[data-sel]');
    if (!sel) return;
    ui.selects[sel.dataset.sel!] = sel.value;
    render();
  });

  panel.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!btn || btn.disabled || !ui.view) return;
    const arg = btn.dataset.arg ?? '';
    switch (btn.dataset.act) {
      case 'roll':
        send({ type: 'rollDice' });
        break;
      case 'step': {
        const draft = ui[btn.dataset.draft as 'give' | 'get' | 'discard'];
        const r = btn.dataset.res as Resource;
        draft[r] = Math.max(0, draft[r] + Number(btn.dataset.delta));
        render();
        break;
      }
      case 'discard':
        send({ type: 'discard', resources: ui.discard });
        break;
      case 'steal':
        send({ type: 'moveRobber', hex: ui.robberHex, victim: Number(arg) });
        break;
      case 'robber-back':
        ui.robberHex = null;
        render();
        break;
      case 'mode':
        ui.mode = (arg || null) as UiState['mode'];
        render();
        break;
      case 'buy-dev':
        send({ type: 'buyDevCard' });
        break;
      case 'bank':
        send({ type: 'bankTrade', give: ui.selects.bankGive, get: ui.selects.bankGet });
        break;
      case 'offer':
        send({ type: 'offerTrade', give: ui.give, get: ui.get });
        break;
      case 'respond':
        send({ type: 'respondTrade', accept: arg === '1' });
        break;
      case 'confirm':
        send({ type: 'confirmTrade', with: Number(arg) });
        break;
      case 'cancel-trade':
        send({ type: 'cancelTrade' });
        break;
      case 'play':
        if (arg === 'knight') send({ type: 'playKnight' });
        if (arg === 'roadBuilding') send({ type: 'playRoadBuilding' });
        if (arg === 'yearOfPlenty') send({ type: 'playYearOfPlenty', resources: [ui.selects.yop1, ui.selects.yop2] });
        if (arg === 'monopoly') send({ type: 'playMonopoly', resource: ui.selects.mono });
        break;
      case 'end':
        send({ type: 'endTurn' });
        break;
    }
  });

  render();
  connect();
}

// ---------- routing ----------

const root = document.getElementById('app')!;
const match = location.pathname.match(/^\/game\/([\w-]+)/);
if (match) startGame(root, match[1], new URLSearchParams(location.search).get('seat'));
else renderLobby(root);
